import * as vp from 'vopay';
import { Box, Title, Table, TextField, Card, Icon } from 'vopay';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import userPostDeleteUser from '@/hooks/useApi/usePostDeleteAccountUser';
import DeleteUserContext, { DeleteUserCtx } from './DeleteUser/DeleteUserContext';
import DeleteUserDialogue from './DeleteUser/DeleteUserDialogue';
import { DeleteUserDialogueHandler, DialogueHandlerProps } from './types';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { Button } from 'UI';
import useResetPassword from './useResetPassword';
import AccountUserPermissionsDrawer from './UserPermissions/AccountUserPermissionsDrawer';
import UserRolesContainer from './UserRoles/UserRolesContainer';
import CreateRoleDrawer from './UserRoles/CreateRoleDrawer';
import useFetchAccountUserRoles from '@/hooks/useApi/useFetchAccountUserRoles';


const UsersList: FC = () => {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { resetPassword } = useResetPassword();
  const { userSettings } = useUserSettingsProvider();
  const [shouldFetchUsers, setFetchUsers] = useState(true);
  const [DeleteUserError, setDeleteUserError] = useState('');
  const [showDeleteUserErrorAlert, setShowDeleteUserErrorAlert] = useState(false);
  const [showDeleteUserSuccessAlert, setShowDeleteUserSuccessAlert] = useState(false);
  const [showDeleteUserConfirmationAlert, setshowDeleteUserConfirmationAlert] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [shouldDeleteUser, setShouldDeleteUser] = useState(false);
  const [DeleteUserResponse, deleteUser] = userPostDeleteUser({});
  const [users, fetchUsers] = useFetchAccountUsers({});
  const [filteredUsers, setFilteredUsers] = useState<vp.AccountUser[]>([]);
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState<string>();
  const [isCreateRoleDrawerOpen, setIsCreateRoleDrawerOpen] = useState(false);

  const { roles, fetching: fetchingRoles, fetchAccountUserRoles } = useFetchAccountUserRoles();

  useEffect(() => {
    if (!shouldFetchUsers) return;

    fetchUsers({
      Timezone: userSettings.timezone,
    });
  }, [shouldFetchUsers]);

  // Fetch roles once the user's view/manage permissions are known.
  // NOTE: fetchAccountUserRoles is intentionally NOT a dependency — the hook
  // re-creates it every render (Direct Pattern, no useCallback), so listing it
  // here caused an infinite fetch loop (setFetching → re-render → new fn ref →
  // effect re-runs). Re-run only when the permission booleans actually change.
  useEffect(() => {
    if (permissions.ViewUserRoles || permissions.ManageUserRoles) {
      fetchAccountUserRoles().catch(() => {
        // Deliberately silent: this is a non-critical background fetch on mount.
        // The hook already logs for developers; the roles section just stays
        // empty if it fails, so no user-facing toast is warranted here.
      });
    }
  }, [permissions.ViewUserRoles, permissions.ManageUserRoles]);
  useEffect(() => {
    const filteredUsersToSet = Object.values(users.Users || {}).filter(
      (user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filteredUsersToSet);
  }, [searchTerm, users]);

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.description', {
        defaultValue: 'This will send a secure password reset link to the user’s email address.',
      }),
      {
        abortLabel: t('ResetPassword.cancelButton', { defaultValue: 'Cancel Button' }),
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
      },
    );

    if (result === 'confirm') {
      try {
        await resetPassword({
          Username: userEmail,
        });
        toast.success(t('ResetPassword.success', { defaultValue: 'The link was sent successfully.' }));
      } catch (err) {
        console.error(err);
        toast.neutral(t('ResetPassword.fail', { defaultValue: 'Unable to send the link.' }));
      }
    }
  };

  const tableProps: vp.TableProps<vp.AccountUser> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      { id: 'Username', header: t('table.email') || '', align: 'left', field: 'Username' },
      { id: 'DateAdded', header: t('table.dateAdded') || '', align: 'left', field: 'DateAdded' },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data: user }) => (
          <Box jc="fe" gap="sm">
            {permissions.ManageUserPermissions && (
              <Button
                size="small"
                onClick={() => {
                  setPermissionsEmail(user.Username);
                  setIsPermissionsDrawerOpen(true);
                }}
              >
                {t('actions.permissions')}
              </Button>
            )}
            {permissions.AddUsers && (
              <>
                <Button
                  size="small"
                  onClick={() => handleResetPasswordDialog(user.Username)}
                >
                  {t('actions.resetPassword', { defaultValue: 'Reset Password' })}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEmailAddress(user.Username);
                    setshowDeleteUserConfirmationAlert(true);
                  }}
                >
                  {t('actions.remove')}
                </Button>
              </>
            )}
          </Box>
        ),
      },
    ],
    rows: filteredUsers.map((user) => ({
      id: user.Username,
      data: user,
    })),
  };

  const DeleteUser: DeleteUserCtx = {
    showDeleteUserErrorAlert,
    showDeleteUserSuccessAlert,
    showDeleteUserConfirmationAlert,
    DeleteUserError,
  };

  useEffect(() => {
    if (shouldDeleteUser) {
      setShouldDeleteUser(false);
      const { Success, ErrorMessage } = DeleteUserResponse;
      if (Success === true) {
        setShowDeleteUserSuccessAlert(true);
      } else {
        setDeleteUserError(ErrorMessage);
        setShowDeleteUserErrorAlert(true);
      }
    }
  }, [DeleteUserResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<DeleteUserDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: DeleteUserDialogueHandler = {
        delete: {
          success: {
            default: () => {
              setShowDeleteUserSuccessAlert(false);
              setFetchUsers(true);
              fetchUsers({
                Timezone: userSettings.timezone,
              });
            },
          },
          fail: {
            default: () => {
              setShowDeleteUserErrorAlert(false);
              setDeleteUserError('');
            },
          },
        },
        alertDelete: {
          success: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);

              const DeleteUserRequest = {
                EmailAddress: emailAddress,
              };
              setShouldDeleteUser(true);
              deleteUser(DeleteUserRequest);
            },
          },
          fail: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  return (
    <div>
      <DeleteUserContext.Provider value={DeleteUser}>
        <Box mb="md" jc="sb">
          <Title title={t('title')} />
          <Box gapSize="16px" ai="center">
            <TextField
              key="UserSearchKeyword"
              name="UserSearchKeyword"
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              variant="outlineShadow"
              elementLeft={
                <div style={{ marginLeft: '14px' }}>
                  <Icon icon="search" width="17px" height="17px" color="none" />
                </div>
              }
            />
            {permissions.AddUsers && (
              <Button
                id="showHideAccountCredentials"
                onClick={() => {
                  history.push('/users/create');
                }}
                colorScheme="primary"
              >
                {t('actions.addUser')}
              </Button>
            )}
          </Box>
        </Box>
        <UserRolesContainer
          onCreateRoleClick={() => setIsCreateRoleDrawerOpen(true)}
          roles={roles}
          fetching={fetchingRoles}
        />
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Table {...tableProps} />
        </Card>
        <DeleteUserDialogue handleDialogue={handlers.handleDialogue} />
      </DeleteUserContext.Provider>
      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
      />
      <CreateRoleDrawer
        isOpen={isCreateRoleDrawerOpen}
        onClose={() => setIsCreateRoleDrawerOpen(false)}
        onRoleCreated={() => {
          fetchAccountUserRoles().catch(() => {
            // Errors are already logged in the hook.
          });
        }}
      />
    </div>
  );
};
export default UsersList;
